import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, TrendingUp, AlertTriangle, Target, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CardSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";

const SCALE_LABELS = ["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"];

export default function AssessmentRun() {
  const { type, mode } = useParams(); // mode: run | result (from route)
  const nav = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const isResultMode = mode === "result";

  useEffect(() => {
    if (isResultMode) {
      api.get(`/assessments/${type}/result`).then((r) => setResult(r.data)).catch(() => nav(`/app/assessments/${type}`));
      return;
    }
    api.get(`/assessments/${type}/questions`).then((r) => {
      setQuestions(r.data);
      setAnswers(new Array(r.data.questions.length).fill(0));
    }).catch(() => {
      toast.error("Assessment not found");
      nav("/app/assessments");
    });
  }, [type, mode, isResultMode, nav]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/assessments/${type}/submit`, { answers });
      setResult(data);
      toast.success("Result computed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const pick = (val) => {
    const next = [...answers];
    next[idx] = val;
    setAnswers(next);
    if (idx < (questions?.questions?.length || 0) - 1) {
      setTimeout(() => setIdx(idx + 1), 180);
    }
  };

  const total = questions?.questions?.length || 0;
  const progressPct = total ? Math.round(((idx + 1) / total) * 100) : 0;
  const answered = answers.filter((a) => a > 0).length;
  const allDone = answered === total && total > 0;

  if (result) return <AssessmentResult data={result} onRetake={() => nav(`/app/assessments/${type}`)} />;
  if (!questions) return <div className="space-y-4"><CardSkeleton /><CardSkeleton /></div>;

  const q = questions.questions[idx];

  return (
    <div data-testid="assessment-run-page" className="max-w-3xl mx-auto">
      <PageHeader
        eyebrow={questions.title}
        title={`Question ${idx + 1} of ${total}`}
        subtitle={questions.description}
      />

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
          <span>Progress</span>
          <span data-testid="assessment-progress">{answered}/{total}</span>
        </div>
        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="bg-card border border-border rounded-xl p-8"
          data-testid="assessment-question-card"
        >
          <p className="font-display font-medium text-xl md:text-2xl text-foreground tracking-tight leading-relaxed mb-8">
            {q.text}
          </p>
          <div className="grid grid-cols-5 gap-2">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                data-testid={`assessment-answer-${v}`}
                onClick={() => pick(v)}
                className={`px-3 py-3 border rounded-lg transition-all font-mono text-sm font-medium text-center focus-visible:ring-2 focus-visible:ring-ring ${
                  answers[idx] === v
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground hover:bg-primary/5"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-2 mt-2 text-[10px] font-mono uppercase tracking-wide text-muted-foreground text-center">
            {SCALE_LABELS.map((l) => (
              <span key={l} className="truncate" title={l}>{l.split(" ")[0]}</span>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="flex items-center justify-between mt-6">
        <Button
          variant="ghost"
          onClick={() => setIdx(Math.max(0, idx - 1))}
          disabled={idx === 0}
          data-testid="assessment-prev"
          className="gap-2"
        >
          <ArrowLeft size={14} /> Previous
        </Button>
        {idx < total - 1 ? (
          <Button
            variant="outline"
            onClick={() => setIdx(idx + 1)}
            disabled={!answers[idx]}
            data-testid="assessment-next"
            className="gap-2"
          >
            Next <ArrowRight size={14} />
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={!allDone || submitting}
            data-testid="assessment-submit"
            className="gap-2"
          >
            {submitting ? "Computing…" : "Submit"} <CheckCircle2 size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

function AssessmentResult({ data, onRetake }) {
  const scores = data.scores || {};
  const traits = data.trait_key_map || {};
  const interp = data.interpretation || {};
  const sortedTraits = useMemo(() => Object.entries(scores).sort((a, b) => b[1] - a[1]), [scores]);

  return (
    <div data-testid="assessment-result">
      <PageHeader
        eyebrow="Assessment result"
        title={data.title}
        subtitle={interp.summary}
        actions={<Button variant="outline" onClick={onRetake} data-testid="assessment-retake">Retake</Button>}
      />

      {/* Trait bars */}
      <section className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="assessment-result-traits">
        <h3 className="font-display font-bold text-lg mb-4">Your scores</h3>
        <div className="space-y-3">
          {sortedTraits.map(([key, pct]) => (
            <div key={key}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-foreground font-medium">{traits[key] || key}</span>
                <span className="font-mono font-semibold text-foreground">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <motion.div
                  className="h-full bg-primary rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <ResultList icon={TrendingUp} title="Strengths" items={interp.strengths || []} tone="success" testId="strengths" />
        <ResultList icon={AlertTriangle} title="Watchouts" items={interp.watchouts || []} tone="warning" testId="watchouts" />
      </div>

      {(interp.career_families || []).length > 0 && (
        <section className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="assessment-result-careers">
          <div className="flex items-center gap-2 mb-4">
            <Target size={16} className="text-primary" />
            <h3 className="font-display font-bold text-lg">Best-fit career families</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {interp.career_families.map((c, i) => {
              const name = typeof c === "string" ? c : (c.name || c.role || "");
              const reason = typeof c === "string" ? "" : (c.reason || c.description || "");
              return (
                <div key={i} className="border border-border rounded-lg p-4">
                  <div className="font-semibold text-foreground text-sm mb-1">{name}</div>
                  {reason && <div className="text-xs text-muted-foreground leading-relaxed">{reason}</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {(interp.next_actions || []).length > 0 && (
        <section className="bg-card border border-border rounded-xl p-6" data-testid="assessment-result-actions">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-primary" />
            <h3 className="font-display font-bold text-lg">Do this next</h3>
          </div>
          <ul className="space-y-2">
            {interp.next_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <ArrowUpRight size={14} className="text-primary mt-1 shrink-0" />
                <span className="text-foreground leading-relaxed">{typeof a === "string" ? a : (a.text || a.action || "")}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ResultList({ icon: Icon, title, items, tone, testId }) {
  const toneCls = tone === "warning" ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--success))]";
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid={`result-${testId}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={toneCls} />
        <h3 className="font-display font-bold text-base">{title}</h3>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">—</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="text-sm text-foreground leading-relaxed">• {typeof it === "string" ? it : (it.text || JSON.stringify(it))}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
